/* The supplier exchange. One list the reader may be on either side of, so every row has to say
   which side that is — the layout leans on words rather than on colour for it (ADR-0007). */

/* The tab area and its layouts. `.stack` is a column of full-width blocks; `.split` is the exchange
   list beside the permissions; `.grid12` is the main grid again, for panels that place themselves
   with a `column` prop (Purchases, Responsiveness). A panel is a column flex box with
   `min-height: 0`, so in a stack it must keep its content height or it is squeezed. */
.tabArea {
  grid-column: 1 / -1;
  min-width: 0;
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  min-width: 0;
}

.stack > * {
  flex: none;
}

.split {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: var(--gap);
  align-items: start;
  min-width: 0;
}

.grid12 {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: var(--gap);
  min-width: 0;
}

@media (max-width: 1100px) {
  .split {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 900px) {
  .grid12 {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* A row somebody has to act on carries an edge and a spoken cue (`.cue`), never colour alone. */
.rowCue {
  padding-left: 10px;
  border-left: 3px solid var(--warn-line);
}

.cue {
  font-weight: 650;
  color: var(--ink);
}

/* The honesty copy, kept and ranked: collapsed, one line, one click away. */
.limits {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid var(--line);
  font-size: var(--t-min);
  color: var(--ink-2);
}

.limits > summary {
  cursor: pointer;
  font-weight: 600;
  color: var(--ink-2);
}

.limits > summary:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.list,
.agreements {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.responsiveness,
.responseSource {
  min-width: 0;
  padding: 10px 0;
  overflow-wrap: anywhere;
  border-bottom: 1px solid var(--line);
}

.responseMetrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 180px), 1fr));
  gap: 12px;
  font-size: var(--t-min);
}

.responseMetrics dd {
  margin: 4px 0 0;
  font-family: var(--mono);
  font-size: var(--t-dense);
}

.responseSource {
  font-family: var(--mono);
  font-size: var(--t-min);
}

.row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 280px;
  gap: 12px;
  padding: 11px 0;
  border-bottom: 1px solid var(--line);
}

/* A row with no side column (the supplier account page's lists): without this the 280px track
   below is reserved for nothing and the content is squeezed into whatever is left. */
.rowSingle {
  grid-template-columns: minmax(0, 1fr);
}

.main,
.side {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.title {
  font-size: var(--t-body);
  font-weight: 650;
  color: var(--ink);
  text-transform: capitalize;
}

.meta {
  font-size: var(--t-min);
  color: var(--muted);
}

.note {
  font-size: var(--t-min);
  color: var(--ink-2);
}

.answer {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 4px;
  padding: 7px 9px;
  border-left: 2px solid var(--accent);
  background: var(--panel-2);
}

.figure {
  font-family: var(--mono);
  font-size: var(--t-dense);
  color: var(--ink);
}

.basis {
  font-size: var(--t-min);
  color: var(--ink-2);
  line-height: 1.5;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

/* A role that cannot accept is told who can, never shown a control that predictably fails. */
.unauthorized {
  margin: 0;
  padding: 9px 11px;
  border: 1px solid var(--warn-line);
  border-radius: 7px;
  background: var(--warn-w);
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

/* The "no permission" notice holds a heading sentence, a list of why-not reasons and the remedy. */
.unauthorized p {
  margin: 0 0 6px;
}

.unauthorized p:last-child {
  margin-bottom: 0;
}

/* One line per agreement that does not permit a copy, with the one reason it fails. */
.whyNot {
  margin: 0 0 6px;
  padding-left: 18px;
}

.whyNot li + li {
  margin-top: 3px;
}

.agreement {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 9px 0;
  border-bottom: 1px solid var(--line);
}

.uses {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.use {
  padding: 2px 7px;
  border: 1px solid var(--line);
  border-radius: 999px;
  font-size: var(--t-min);
  color: var(--ink-2);
  text-transform: capitalize;
}

.revoked {
  font-size: var(--t-min);
  line-height: 1.5;
  color: var(--ink-2);
}

@media (max-width: 900px) {
  .row {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* The division behind a share (S3-02). Set apart from the figure so a reader sees that it explains
   it rather than adds to it, and in the mono face because it is arithmetic to be checked. */
.allocation {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 5px;
  padding-top: 5px;
  border-top: 1px dashed var(--line);
}

.sum {
  font-family: var(--mono);
  font-size: var(--t-min);
  color: var(--ink-2);
}
